import { useRef, useState } from "react";
import { Camera, Loader2, X, FileCheck2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { fileToJpegBase64 } from "@/lib/veeder";
import { parseChartText } from "@/lib/chart";
import { formatGal, formatToEighths, tankLabel } from "@/lib/tank";

const toText = (rows) => (rows || []).map(([i, g]) => `${i}\t${g}`).join("\n");

export function ChartEntry({ tank, t, onSave, onClose }) {
  const [text, setText] = useState(toText(tank.chart));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const input = useRef(null);
  const { rows, ok, bad } = parseChartText(text);
  const top = rows.length ? rows[rows.length - 1] : null;
  const offCap = ok && top && tank.capacity && Math.abs(top[1] - tank.capacity) / tank.capacity > 0.1;

  const scan = async (file) => {
    if (!file) return;
    setBusy(true); setErr("");
    try { const { rows: got } = await api.scanChart(await fileToJpegBase64(file, 2000)); setText(toText(got)); }
    catch (e) { setErr(apiError(e) || t("Couldn't read that photo. Try a sharper, straight-on photo or type the rows.")); }
    finally { setBusy(false); if (input.current) input.current.value = ""; }
  };

  return (
    <div className="modal-backdrop chart-backdrop" data-testid="chart-entry" onClick={onClose}>
      <div className="modal editor-modal" onClick={(e) => e.stopPropagation()}>
        <div className="section-title">{t("Real chart for {l}", { l: tankLabel(tank.label) || "tank" })}</div>
        <div className="card-sub">{t("Use the chart the station or tank maker gave you. One line per row: inches, then gallons.")}</div>
        <button type="button" className="ghost-btn edit-add" data-testid="chart-scan" disabled={busy} onClick={() => input.current?.click()}>
          {busy ? <Loader2 size={16} className="spin" /> : <Camera size={16} />}{t("Take a photo of the chart")}</button>
        <input ref={input} type="file" accept="image/*" hidden data-testid="chart-scan-input" onChange={(e) => scan(e.target.files?.[0])} />
        <textarea className="text-input chart-text mono" data-testid="chart-text" value={text} placeholder={"1\t21\n2\t58\n3\t106"} onChange={(e) => setText(e.target.value)} />
        {rows.length > 0 && (ok ? (
          <div className="chart-ok" data-testid="chart-summary"><FileCheck2 size={14} />
            {t("{n} rows · {a} to {b} · up to {g} gal", { n: rows.length, a: formatToEighths(rows[0][0]), b: formatToEighths(top[0]), g: formatGal(top[1]) })}</div>
        ) : <div className="form-error" data-testid="chart-invalid">{bad ? t("{n} line(s) couldn't be read. Each line needs inches and gallons.", { n: bad }) : t("Rows must go up: more inches always means more gallons. Check for typos.")}</div>)}
        {offCap && <div className="form-error" data-testid="chart-cap-warn">{t("This chart tops out at {g} gal but the tank is set to {c} gal. Make sure it's the right chart.", { g: formatGal(top[1]), c: formatGal(tank.capacity) })}</div>}
        {err && <div className="form-error" data-testid="chart-error">{err}</div>}
        {busy && <div className="card-sub">{t("Reading the chart… always check the rows against the paper chart.")}</div>}
        <div className="modal-actions">
          {tank.chart?.length > 0 && <button type="button" className="ghost-btn" data-testid="chart-remove" onClick={() => onSave(null)}><X size={14} />{t("Remove")}</button>}
          <button type="button" className="ghost-btn" data-testid="chart-cancel" onClick={onClose}>{t("Cancel")}</button>
          <button type="button" className="primary-btn" data-testid="chart-save" disabled={!ok} onClick={() => onSave(rows)}>{t("Use this chart")}</button>
        </div>
      </div>
    </div>
  );
}
